import * as Graph from '@microsoft/microsoft-graph-types';
import { z } from 'zod';
import { List } from './list';
import { ColumnsFromList } from './queries';

export interface Options {
  type: 'string' | 'array' | 'lookup' | 'boolean' | 'date';
  definition: Graph.ColumnDefinition;
}

export type ColumnType<O extends Options, E = O['type']> = E extends 'array'
  ? string[]
  : E extends 'string'
  ? string
  : E extends 'lookup'
  ? string
  : E extends 'boolean'
  ? boolean
  : E extends 'date'
  ? string | undefined
  : never;

export interface Column<
  N extends string = string,
  P extends z.ZodType<any, any, any> = z.ZodType<any, any, any>,
  S extends z.ZodType<any, any, any> = z.ZodType<any, any, any>,
  O extends Options = Options
> {
  name: N;
  parse: P;
  serialize: S;
  options?: O;
}

type ColumnBuilder = <
  N extends string,
  O extends Options,
  T extends ColumnType<O>,
  P extends z.ZodType<any, any, T>,
  S extends z.ZodType<T, any, any>
>(
  name: N,
  parse: P,
  serialize: S,
  options?: O
) => Column<N, P, S, O>;

export const column: ColumnBuilder = (name, parse, serialize, options) => ({
  name,
  parse,
  serialize,
  options
});

export const SemiColonDelimited = {
  Parser: z.string().transform((s: string) => s.split(';')),
  Serializer: z.array(z.string()).transform((s: string[]) => s.join(';'))
};

export function char<S extends string>(name: S) {
  return column(name, z.string(), z.string(), {
    type: 'string',
    definition: {
      indexed: false,
      hidden: false,
      text: {
        textType: 'plain',
        allowMultipleLines: true,
        appendChangesToExistingText: false
      }
    }
  });
}

export function text<S extends string>(name: S) {
  return column(name, z.string(), z.string(), {
    type: 'string',
    definition: {
      indexed: false,
      hidden: false,
      text: {
        textType: 'plain',
        allowMultipleLines: false,
        appendChangesToExistingText: false,
        maxLength: 255
      }
    }
  });
}

export function date<S extends string>(name: S) {
  return column(
    name,
    z.string().transform((s: string) => {
      if (!s) return undefined;
      return new Date(s);
    }),
    z
      .date()
      .optional()
      .transform((d: Date | undefined) => {
        if (!d) return undefined;
        return d.toISOString();
      }),
    {
      type: 'date',
      definition: {
        dateTime: {
          displayAs: 'default',
          format: 'dateTime'
        }
      }
    }
  );
}

export function choice<S extends string>(name: S) {
  return column(name, z.string(), z.string(), {
    type: 'string',
    definition: {
      choice: {
        // Does not allow the user to enter a custom value
        allowTextEntry: true,
        // The choices that the user can select from
        choices: [],
        // This allows for just one choice to be selected
        displayAs: 'dropDownMenu'
      }
    }
  });
}

export function boolean<S extends string>(name: S) {
  return column(name, z.boolean(), z.boolean(), { definition: { boolean: {} }, type: 'boolean' });
}

export function array<S extends string>(name: S) {
  return column(name, SemiColonDelimited.Parser, SemiColonDelimited.Serializer, {
    type: 'string',
    definition: {
      indexed: false,
      hidden: false,
      text: {
        allowMultipleLines: true,
        appendChangesToExistingText: false
      }
    }
  });
}

export function lookup<S extends string, ForeignList extends List, ForeignField extends ColumnsFromList<ForeignList>>(
  name: S,
  list: ForeignList,
  field: ForeignField
) {
  return column(name, z.string(), z.string(), {
    type: 'lookup',
    definition: {
      lookup: {
        allowMultipleValues: false,
        allowUnlimitedLength: false,
        columnName: field.name,
        listId: list.meta.id
      }
    }
  });
}

export function lookups<S extends string, ForeignList extends List, ForeignField extends ColumnsFromList<ForeignList>>(
  name: S,
  list: ForeignList,
  field: ForeignField
) {
  return column(name, z.array(z.string()), z.array(z.string()), {
    type: 'array',
    definition: {
      lookup: {
        allowMultipleValues: true,
        allowUnlimitedLength: false,
        columnName: field.name,
        listId: list.meta.id
      }
    }
  });
}
